import React, { useEffect, useState } from 'react';
import {
  Modal,
  Card,
  Row,
  Col,
  Button,
  Radio,
  DatePicker,
  TimePicker,
  Select,
  Typography,
  Statistic,
  Alert,
  Space,
  Divider,
  Checkbox,
  Tag,
  message
} from 'antd';
import {
  SendOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  TeamOutlined,
  ReloadOutlined
} from '@ant-design/icons';
import moment from 'moment';
import axiosInstance from '../../../../config/axiosConfig';
import OptOutService from '../../../../services/optoutService';
import './CampaignModals.css';

const { Text, Title } = Typography;
const { Option } = Select;

const SendCampaignModal = ({ visible, onCancel, onSend, campaign, loading, campaignId="" }) => {
  const [sendType, setSendType] = useState('immediate');
  const [scheduleDate, setScheduleDate] = useState(null);
  const [scheduleTime, setScheduleTime] = useState(null);
  const [repeatOption, setRepeatOption] = useState('none');
  const [campaignStats, setCampaignStats] = useState(null);
  const [pricePerMessage, setPricePerMessage] = useState(0);
  const [actualTotalContacts, setActualTotalContacts] = useState(0);
  const [actualEstimatedCost, setActualEstimatedCost] = useState(0);
  const [rateLoading, setRateLoading] = useState(false);
  const [optOutSkipCount, setOptOutSkipCount] = useState(0);

  // Plan-based quota state
  const [planStatus, setPlanStatus] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);

  // Phase 2.5 — BYO Webhook authorization
  const isByo = campaign?.source === 'byo_webhook';
  const [byoAuthorized, setByoAuthorized] = useState(false);
  useEffect(() => { if (visible) setByoAuthorized(false); }, [visible, campaign?.id]);

  useEffect(
    () => {
      if (visible) {
        // Reset state when modal opens to ensure fresh data
        setSendType('immediate');
        setScheduleDate(null);
        setScheduleTime(null);
        setRepeatOption('none');
        setCampaignStats(null);
        setActualTotalContacts(0);
        setActualEstimatedCost(0);
        setRateLoading(true);
        setOptOutSkipCount(0);
        
        if (campaignId) {
          fetchCampaignStats(campaignId);
          fetchOptOutSkipCount(campaignId);
        }
        fetchPlanStatus();
      }
  }, [visible, campaignId]);

  const getScheduledMoment = () => {
    if (!scheduleDate || !scheduleTime) return null;
    return scheduleDate
      .clone()
      .hour(scheduleTime.hour())
      .minute(scheduleTime.minute())
      .second(0)
      .millisecond(0);
  };

  const getScheduledAtIso = () => {
    const scheduledMoment = getScheduledMoment();
    if (!scheduledMoment) return null;
    return new Date(scheduledMoment.valueOf()).toISOString();
  };

  // Load message rate after campaign stats are fetched (to use actual recipient count)
  useEffect(() => {
    if (visible && campaignStats !== null) {
      if (isByo) {
        // BYO cost is TBD — no rate calculation needed, just stop the spinner
        setRateLoading(false);
      } else {
        loadMessageRate();
      }
    }
  }, [visible, campaignStats, campaign?.templateCategory, campaign?.template?.category]);
// console.log('Campaign campaignStats:', campaignStats);

  const fetchPlanStatus = async () => {
    try {
      setPlanLoading(true);
      const res = await axiosInstance.get('/wallet/plan-status');
      const entity = res.data?.entity;
      if (entity) {
        setPlanStatus(entity);
      }
    } catch (error) {
      console.error('Error fetching plan status:', error);
    } finally {
      setPlanLoading(false);
    }
  };

  const loadMessageRate = async () => {
    try {
      setRateLoading(true);

      const actualRecipientCount = campaignStats?.total || campaign?.recipients || 0;
      const templateCategory = (campaign?.templateCategory || campaign?.template?.category || 'MARKETING').toUpperCase();
      const ratedetails = campaign?.ratedetails;

      // Build country-code groups from ratedetails (phone codes like "+91")
      const contactsByPhone = {};
      if (ratedetails?.contactsdetails && Array.isArray(ratedetails.contactsdetails)) {
        ratedetails.contactsdetails.forEach((d) => {
          const phone = (d.countrycode || '+91').replace(/^\+/, '+');
          const formattedPhone = phone.startsWith('+') ? phone : `+${phone}`;
          contactsByPhone[formattedPhone] = (contactsByPhone[formattedPhone] || 0) + Number(d.count || 0);
        });
      }

      // If no country breakdown, treat all recipients as India (+91) default
      if (Object.keys(contactsByPhone).length === 0) {
        contactsByPhone['+91'] = actualRecipientCount;
      }

      // Fetch country list to map phone codes → ISO codes
      let phoneToISO = {};
      try {
        const countriesRes = await axiosInstance.get('/v1/pricing/countries');
        const countries = countriesRes.data?.entity || [];
        countries.forEach((c) => {
          phoneToISO[c.phoneCode] = c.code; // e.g. "+91" → "IN"
        });
      } catch (_) {
        // Fallback: assume India
        phoneToISO['+91'] = 'IN';
      }

      // Fetch Meta WhatsApp rate for each unique country
      let totalCost = 0;
      let totalContacts = 0;
      const pricingPromises = Object.entries(contactsByPhone).map(async ([phoneCode, count]) => {
        const isoCode = phoneToISO[phoneCode] || 'IN';
        try {
          const { data } = await axiosInstance.get('/v1/pricing', {
            params: { countryCode: isoCode, category: templateCategory, currency: 'INR' },
          });
          if (data.status && data.statuscode === 200 && data.entity) {
            const rate = Number(data.entity.price || 0);
            totalCost += rate * count;
            totalContacts += count;
            return rate;
          }
        } catch (_) { /* skip this country on error */ }
        return 0;
      });

      const rates = await Promise.all(pricingPromises);

      // Calculate weighted average per-message rate
      const perMessageRate = totalContacts > 0 ? totalCost / totalContacts : 0;

      setPricePerMessage(perMessageRate);
      setActualTotalContacts(actualRecipientCount);
      setActualEstimatedCost(perMessageRate * actualRecipientCount);

      console.log('Rate calculation (Meta pricing):', {
        actualRecipientCount,
        perMessageRate,
        estimatedCost: perMessageRate * actualRecipientCount,
        contactsByPhone,
        rates,
      });
    } catch (error) {
      console.error('Error loading message rate:', error);
      const actualRecipientCount = campaignStats?.total || campaign?.recipients || 0;
      setPricePerMessage(0);
      setActualTotalContacts(actualRecipientCount);
      setActualEstimatedCost(0);
    } finally {
      setRateLoading(false);
    }
  };

  const fetchCampaignStats = async (campaignId) => {
    try {
           const response = await axiosInstance.post('/campaign/transactions', {
        campaignid: campaignId
      });
      console.log('Campaign stats response:', response.data);
      if (response.data.statuscode === 200) {
        setCampaignStats(response.data.entity);
      } else if (response.data.statuscode === 404) {
        // BYO webhook campaigns have no transactions until sent — this is normal.
        // Set to empty object (not null) so the rateLoading useEffect fires and
        // can immediately stop the spinner via the isByo branch.
        setCampaignStats({});
      } else {
        message.error(response.data.message || 'Failed to load campaign statistics');
        setCampaignStats({});
      }
    } catch (error) {
      setCampaignStats({});
    }
  }

  const fetchOptOutSkipCount = async (cId) => {
    try {
      const data = await OptOutService.getCampaignSkipCount(cId);
      if (data.success) {
        setOptOutSkipCount(data.skippedContacts || data.data?.skippedContacts || 0);
      }
    } catch (err) {
      // Non-critical — don't block the modal if this fails
      console.warn('Could not fetch opt-out skip count:', err);
    }
  };

  const handleSend = () => {
    const scheduledAt = sendType === 'scheduled' ? getScheduledAtIso() : null;

    if (sendType === 'scheduled') {
      if (!scheduledAt) {
        message.error('Please choose a schedule date and time');
        return;
      }
      if (new Date(scheduledAt).getTime() <= Date.now()) {
        message.error('Please choose a future date and time');
        return;
      }
    }

    // For BYO webhook campaigns the recipient count is unknown until send time.
    // Pass hard_send_cap as the ceiling so the backend doesn't reject with "0 recipients".
    const totalContacts = isByo
      ? (campaign.hard_send_cap || 0)
      : (campaignStats?.total || actualTotalContacts || campaign?.recipients || 0);
    const estimatedCost = isByo ? 0 : pricePerMessage * totalContacts;

    const sendData = {
      campaignId: campaign?.id || campaign?.campaignid,
      type: sendType,
      scheduleDate: sendType === 'scheduled' ? scheduleDate : null,
      scheduleTime: sendType === 'scheduled' ? scheduleTime : null,
      repeat: sendType === 'recurring' ? repeatOption : 'none',
      sendMode: sendType === 'scheduled' ? 'scheduled' : 'now',
      scheduledAt,
      totalcontacts: Number(totalContacts),
      // Keep pricepermessage/estimatecost for backward API compat but they won't be used for billing
      pricepermessage: Number(pricePerMessage),
      estimatecost: Number(parseFloat(estimatedCost).toFixed(2)),
      messagecategory: (campaign?.templateCategory || campaign?.template?.category || 'Marketing').toUpperCase()
    };

    console.log('Sending campaign data:', sendData);
    onSend(sendData);
  };

  const isValidSchedule = () => {
    if (sendType === 'immediate') return true;
    if (sendType === 'scheduled') {
      const scheduledAt = getScheduledAtIso();
      return Boolean(scheduledAt && new Date(scheduledAt).getTime() > Date.now());
    }
    if (sendType === 'recurring') {
      return false;
    }
    return false;
  };

  const scheduledMoment = getScheduledMoment();
  const scheduledTimeIsPast = sendType === 'scheduled' && scheduledMoment && scheduledMoment.valueOf() <= Date.now();

  return (
    <Modal
      title={
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <SendOutlined style={{ color: '#52c41a' }} />
          Send Campaign
        </div>
      }
      visible={visible}
      onCancel={onCancel}
      width="90%"
      style={{ maxWidth: 700 }}
      className="campaign-modal-responsive"
      footer={[
        <Button key="cancel" onClick={onCancel}>
          Cancel
        </Button>,
        <Button
          key="send"
          type="primary"
          icon={<SendOutlined />}
          loading={loading}
          disabled={
            !isValidSchedule() ||
            (isByo && !byoAuthorized) ||
            (!isByo && (actualTotalContacts || campaignStats?.total || 0) > 0 && (actualTotalContacts || campaignStats?.total || 0) - optOutSkipCount <= 0)
          }
          onClick={handleSend}
          style={{ backgroundColor: '#52c41a', borderColor: '#52c41a' }}
        >
          {sendType === 'immediate' ? 'Send Now' : 
           sendType === 'scheduled' ? 'Schedule Campaign' : 
           'Setup Recurring Campaign'}
        </Button>
      ]}
    >
      {campaign && (
        <div>
          {/* Campaign Summary */}
          <Card size="small" style={{ marginBottom: 16 }}>
            <Row gutter={[16, 16]}>
              <Col xs={24} sm={24} md={8}>
                <Statistic
                  title="Campaign"
                  value={campaign.name}
                  valueStyle={{ fontSize: 14, fontWeight: 'bold' }}
                />
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 6 }}>
                  {(() => {
                    const cat = (campaign.templateCategory || campaign.template?.category || 'MARKETING').toUpperCase();
                    const colorMap = { MARKETING: 'purple', UTILITY: 'blue', AUTHENTICATION: 'gold' };
                    return (
                      <Tag color={colorMap[cat] || 'default'} style={{ margin: 0 }}>
                        {cat.charAt(0) + cat.slice(1).toLowerCase()}
                      </Tag>
                    );
                  })()}
                  {isByo && <Tag color="purple" style={{ margin: 0 }}>BYO Webhook</Tag>}
                </div>
              </Col>
              {isByo ? (
                <>
                  <Col xs={12} sm={12} md={8}>
                    <Statistic
                      title="Hard Send Cap"
                      value={(campaign.hard_send_cap || 0).toLocaleString()}
                      prefix={<TeamOutlined />}
                    />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Stops fetching after this many sends
                    </Text>
                  </Col>
                  <Col xs={12} sm={12} md={8}>
                    <Statistic
                      title="Estimated Cost"
                      value="TBD"
                      prefix="💰"
                      valueStyle={{ fontSize: 18 }}
                    />
                    <Text type="secondary" style={{ fontSize: 11 }}>
                      Calculated as your webhook returns recipients
                    </Text>
                  </Col>
                </>
              ) : (
                <>
                  <Col xs={12} sm={12} md={8}>
                    <Statistic
                      title="Recipients"
                      value={Math.max(0, (actualTotalContacts || campaignStats?.total || 0) - optOutSkipCount)}
                      prefix={<TeamOutlined />}
                    />
                    {optOutSkipCount > 0 && (
                      <Text type="secondary" style={{ fontSize: 11 }}>
                        {actualTotalContacts || campaignStats?.total || 0} total − {optOutSkipCount} opted out
                      </Text>
                    )}
                  </Col>
                  <Col xs={12} sm={12} md={8}>
                    {(() => {
                      const totalRecip = actualTotalContacts || campaignStats?.total || campaign?.recipients || 0;
                      const effectiveRecip = Math.max(0, totalRecip - optOutSkipCount);
                      const adjustedCost = pricePerMessage * effectiveRecip;
                      return (
                        <>
                          <Statistic
                            title="Estimated Cost"
                            value={rateLoading ? "Loading..." : `₹${adjustedCost.toFixed(2)}`}
                            prefix="💰"
                          />
                          {!rateLoading && pricePerMessage > 0 && (
                            <Text type="secondary" style={{ fontSize: 11 }}>
                              ₹{pricePerMessage.toFixed(4)} × {effectiveRecip} recipient{effectiveRecip !== 1 ? 's' : ''}
                              {optOutSkipCount > 0 && ` (${optOutSkipCount} opted-out excluded)`}
                            </Text>
                          )}
                        </>
                      );
                    })()}
                  </Col>
                </>
              )}
            </Row>
          </Card>

          {/* Phase 2.5 — BYO authorization */}
          {isByo && (
            <Alert
              type="info"
              showIcon
              style={{ marginBottom: 16, borderRadius: 8 }}
              message="Streaming send — actual cost is unknown until completion"
              description={
                <Space direction="vertical" size={6} style={{ width: '100%' }}>
                  <Text style={{ fontSize: 13 }}>
                    Recipients will be fetched live from <Text code>{campaign.audience_webhook_url || 'your webhook'}</Text>.
                    Sending continues until the webhook returns <Text code>null</Text> or the hard cap of{' '}
                    <Text strong>{(campaign.hard_send_cap || 0).toLocaleString()}</Text> messages is reached.
                  </Text>
                  <Checkbox
                    checked={byoAuthorized}
                    onChange={(e) => setByoAuthorized(e.target.checked)}
                  >
                    <Text strong>I authorize MessageOS to send up to my hard cap or until my webhook returns null.</Text>
                  </Checkbox>
                </Space>
              }
            />
          )}

          {/* Opt-Out Skip Warning */}
          {optOutSkipCount > 0 && (
            <Alert
              message={`${optOutSkipCount} contact${optOutSkipCount > 1 ? 's' : ''} will be skipped (opted out of ${(campaign?.templateCategory || campaign?.template?.category || 'marketing').toLowerCase()})`}
              description={
                <Text style={{ fontSize: 13 }}>
                  These contacts have opted out of {(campaign?.templateCategory || campaign?.template?.category || 'marketing').toLowerCase()} campaigns.
                  They may still receive other category campaigns. They can re-join by sending "START".
                </Text>
              }
              type="warning"
              showIcon
              style={{ marginBottom: 16, borderRadius: 8 }}
            />
          )}

          {/* Balance Validation — Plan-based quota check */}
          {(() => {
            // Plan-based quota check
            if (planStatus && planStatus.has_active_plan === true) {
              const wa = planStatus.whatsapp || {};
              if (!wa.is_unlimited && wa.campaigns_remaining !== null && wa.campaigns_remaining !== undefined && wa.campaigns_remaining <= 0) {
                return (
                  <Alert
                    message="WhatsApp Campaign Quota Exhausted"
                    description={
                      <div>
                        <p>You've used all {wa.campaign_limit || 0} WhatsApp campaigns in your current plan cycle.</p>
                        <p><strong>Suggestions:</strong></p>
                        <ul style={{ marginBottom: 0, paddingLeft: 20 }}>
                          <li>Upgrade to a higher plan for more campaigns</li>
                          <li>Wait for the next billing cycle</li>
                          <li>Contact support for assistance</li>
                        </ul>
                      </div>
                    }
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                );
              }
              if (!wa.is_unlimited && wa.campaigns_remaining !== null && wa.campaigns_remaining <= 3) {
                return (
                  <Alert
                    message="Low Campaign Quota"
                    description={`Only ${wa.campaigns_remaining} WhatsApp campaign${wa.campaigns_remaining !== 1 ? 's' : ''} remaining in your current plan.`}
                    type="warning"
                    showIcon
                    style={{ marginBottom: 16 }}
                  />
                );
              }
            } else if (planStatus && !planStatus.has_active_plan) {
              return (
                <Alert
                  message="No Active Plan"
                  description="You need an active plan to send campaigns. Please purchase a plan first."
                  type="error"
                  showIcon
                  style={{ marginBottom: 16 }}
                />
              );
            }

            return null;
          })()}

          {/* Send Options */}
          <Card size="small" title="Send Options">
            <Radio.Group
              value={sendType}
              onChange={(e) => setSendType(e.target.value)}
              style={{ width: '100%' }}
            >
              <Space direction="vertical" style={{ width: '100%' }}>
                <Radio value="immediate">
                  <div>
                    <Text strong>Send Immediately</Text>
                    <div style={{ fontSize: 12, color: '#666', marginLeft: 20 }}>
                      Campaign will be sent right away to all recipients
                    </div>
                  </div>
                </Radio>

                <Radio value="scheduled">
                  <div>
                    <Text strong>Schedule for Later</Text>
                    <div style={{ fontSize: 12, color: '#666', marginLeft: 20 }}>
                      Choose a future date and time to send
                    </div>
                  </div>
                </Radio>

                <Radio value="recurring" disabled>
                  <div>
                    <Text strong style={{ color: '#999' }}>Recurring Campaign</Text>
                    <div style={{ fontSize: 12, color: '#999', marginLeft: 20 }}>
                      Coming soon - Set up automatic recurring sends
                    </div>
                  </div>
                </Radio>
              </Space>
            </Radio.Group>

            {/* Schedule Options */}
            {(sendType === 'scheduled' || sendType === 'recurring') && (
              <div style={{ marginTop: 16, paddingLeft: 24 }}>
                <Divider style={{ margin: '12px 0' }} />
                <Row gutter={[16, 16]}>
                  <Col xs={24} sm={12}>
                    <div style={{ marginBottom: 8 }}>
                      <CalendarOutlined style={{ marginRight: 8 }} />
                      <Text strong>Select Date</Text>
                    </div>
                    <DatePicker
                      style={{ width: '100%' }}
                      value={scheduleDate}
                      onChange={setScheduleDate}
                      disabledDate={(date) => date && date < moment().startOf('day')}
                      placeholder="Choose date"
                      status={sendType === 'scheduled' && !scheduleDate ? 'error' : undefined}
                    />
                  </Col>
                  <Col xs={24} sm={12}>
                    <div style={{ marginBottom: 8 }}>
                      <ClockCircleOutlined style={{ marginRight: 8 }} />
                      <Text strong>Select Time</Text>
                    </div>
                    <TimePicker
                      style={{ width: '100%' }}
                      value={scheduleTime}
                      onChange={setScheduleTime}
                      format="HH:mm"
                      placeholder="Choose time"
                      status={sendType === 'scheduled' && (!scheduleTime || scheduledTimeIsPast) ? 'error' : undefined}
                    />
                  </Col>
                </Row>

                {sendType === 'scheduled' && scheduledTimeIsPast && (
                  <Alert
                    message="Choose a future time"
                    description="Scheduled campaigns must be set for a future date and time."
                    type="error"
                    showIcon
                    style={{ marginTop: 16 }}
                  />
                )}

                {sendType === 'recurring' && (
                  <div style={{ marginTop: 16 }}>
                    <div style={{ marginBottom: 8 }}>
                      <ReloadOutlined style={{ marginRight: 8 }} />
                      <Text strong>Repeat Frequency</Text>
                    </div>
                    <Select
                      style={{ width: '100%' }}
                      value={repeatOption}
                      onChange={setRepeatOption}
                      placeholder="Select repeat frequency"
                    >
                      <Option value="daily">Daily</Option>
                      <Option value="weekly">Weekly</Option>
                      <Option value="monthly">Monthly</Option>
                    </Select>
                  </div>
                )}
              </div>
            )}
          </Card>

          {/* Warnings and Information */}
          {/* {sendType === 'immediate' && (
            <Alert
              message="Send Campaign Immediately"
              description={
                <div>
                  <p>Your campaign will be sent immediately to all recipients.</p>
                  <p><strong>Note:</strong> This action cannot be undone. Make sure your template and recipient list are correct before proceeding.</p>
                  {planStatus?.has_active_plan && !planStatus?.whatsapp?.is_unlimited && (
                    <p><strong>Campaign quota:</strong> {planStatus.whatsapp?.campaigns_remaining || 0} WhatsApp campaign{(planStatus.whatsapp?.campaigns_remaining || 0) !== 1 ? 's' : ''} remaining this cycle
                      {optOutSkipCount > 0 && <span> ({optOutSkipCount} opted-out contacts excluded)</span>}
                    </p>
                  )}
                  {planStatus?.has_active_plan && planStatus?.whatsapp?.is_unlimited && (
                    <p><strong>Campaign quota:</strong> Unlimited WhatsApp campaigns
                      {optOutSkipCount > 0 && <span> ({optOutSkipCount} opted-out contacts excluded)</span>}
                    </p>
                  )}
                </div>
              }
              type="warning"
              showIcon
              style={{ marginTop: 16 }}
            />
          )} */}

          {sendType === 'scheduled' && scheduleDate && scheduleTime && (
            <Alert
              message="Scheduled Send"
              description={`Your campaign will be sent on ${scheduledMoment.format('MMMM Do, YYYY')} at ${scheduledMoment.format('HH:mm')}`}
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}

          {sendType === 'recurring' && scheduleDate && scheduleTime && repeatOption !== 'none' && (
            <Alert
              message="Recurring Campaign"
              description={`Your campaign will be sent ${repeatOption} starting from ${scheduleDate.format('MMMM Do, YYYY')} at ${scheduleTime.format('HH:mm')}`}
              type="info"
              showIcon
              style={{ marginTop: 16 }}
            />
          )}
        </div>
      )}
    </Modal>
  );
};

export default SendCampaignModal;
